import React, { useState, useEffect } from 'react';
import { Filter, MapPin, Users, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useVenueStore } from '../store/useVenueStore';

// Define countries with flags
const countries = [
  { code: 'se', name: 'Sweden', flag: 'https://i.imgur.com/mnS12vc.jpeg' },
  { code: 'no', name: 'Norway', flag: 'https://i.imgur.com/BW8gsFf.jpeg' },
  { code: 'dk', name: 'Denmark', flag: 'https://i.imgur.com/wLCVBJF.jpeg' },
  { code: 'es', name: 'Spain', flag: '/assets/images/Spain.jpg' }
];

// Define regions - only keep Swedish regions
const regionsByCountry: Record<string, { code: string; name: string }[]> = {
  'se': [
    { code: 'gothenburg', name: 'Gothenburg' },
    { code: 'stockholm', name: 'Stockholm' },
    { code: 'skane', name: 'Skåne' },
    { code: 'north', name: 'Northern Sweden' }
  ]
  // Removed region filters for Denmark, Norway and Spain
};

// Min/max values for capacity slider
const MIN_CAPACITY = 2;
const MAX_CAPACITY = 500;

// Define tick marks for capacity slider
const capacityTicks = [
  MIN_CAPACITY,
  10,
  20,
  50,
  100,
  200,
  MAX_CAPACITY
];

export const FilterBar: React.FC = () => {
  const { t } = useTranslation();
  const { filters, setFilters, updateFilteredVenues } = useVenueStore();
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [capacityValue, setCapacityValue] = useState(
    filters.capacity?.min || MIN_CAPACITY
  );
  
  // Handle country change
  const handleCountryChange = (country: string) => {
    const newCountry = filters.country === country ? null : country;
    setSelectedCountry(newCountry);
    setFilters({
      ...filters,
      country: newCountry,
      region: null // Reset region when country changes
    });
  };
  
  // Reset to show all countries
  const showAllCountries = () => {
    setSelectedCountry(null);
    setFilters({
      ...filters,
      country: null,
      region: null // Reset region as well
    });
  };
  
  // Handle region change
  const handleRegionChange = (region: string) => {
    setFilters({
      ...filters,
      region: filters.region === region ? null : region
    });
  };
  
  // Handle capacity slider change
  const handleCapacityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    setCapacityValue(value);
    
    // Calculate progress percentage for styling
    const progress = ((value - MIN_CAPACITY) / (MAX_CAPACITY - MIN_CAPACITY)) * 100;
    if (e.target.style) {
      e.target.style.setProperty('--range-progress', `${progress}%`);
    }
  };
  
  // Apply capacity filter when slider stops
  const applyCapacityFilter = () => {
    setFilters({
      ...filters,
      capacity: {
        min: capacityValue,
        max: MAX_CAPACITY // Always set max to maximum
      }
    });
  };
  
  // Clear all filters
  const clearFilters = () => {
    setFilters({
      country: null,
      region: null,
      capacity: null
    });
    setSelectedCountry(null);
    setCapacityValue(MIN_CAPACITY);
  };
  
  // Count active filters
  const activeFilterCount = [
    filters.country, 
    filters.region, 
    filters.capacity ? 1 : 0
  ].filter(Boolean).length;
  
  // Set initial progress on component mount
  useEffect(() => {
    const slider = document.querySelector('input[type="range"]');
    if (slider) {
      const progress = ((capacityValue - MIN_CAPACITY) / (MAX_CAPACITY - MIN_CAPACITY)) * 100;
      slider.style.setProperty('--range-progress', `${progress}%`);
    }
  }, [capacityValue]);
  
  useEffect(() => {
    // Update filtered venues whenever filters change
    updateFilteredVenues();
  }, [filters, updateFilteredVenues]);
  
  return (
    <div className="mt-4 bg-white p-4 rounded-lg shadow-md">
      <div className="space-y-4">
        {/* Country filter */}
        <div>
          <h3 className="font-medium text-sm text-gray-700 mb-2 flex items-center">
            <MapPin className="mr-1 h-4 w-4 text-[#fabddf]" />
            {t('filters.country')}
          </h3>
          <div className="flex flex-wrap gap-2">
            {/* All countries option */}
            <button
              className={`px-3 py-1.5 rounded-full text-sm flex items-center ${
                !filters.country
                  ? 'bg-[#fabddf] text-[#2D2F4E]'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              onClick={showAllCountries}
            >
              <Globe className="w-4 h-4 mr-1.5" />
              {t('filters.allCountries')}
            </button>
            
            {countries.map((country) => (
              <button
                key={country.code}
                className={`px-3 py-1.5 rounded-full text-sm flex items-center ${
                  filters.country === country.code
                    ? 'bg-[#fabddf] text-[#2D2F4E]'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                onClick={() => handleCountryChange(country.code)}
              >
                <img src={country.flag} alt="" className="w-4 h-4 rounded-full mr-1.5" />
                {country.name}
              </button>
            ))}
          </div>
        </div>
        
        {/* Region filter - only show for Sweden */}
        {selectedCountry === 'se' && regionsByCountry[selectedCountry] && (
          <div>
            <h3 className="font-medium text-sm text-gray-700 mb-2">
              {t('filters.region')}
            </h3>
            <div className="flex flex-wrap gap-2">
              {regionsByCountry[selectedCountry].map((region) => (
                <button
                  key={region.code}
                  className={`px-3 py-1.5 rounded-full text-sm ${
                    filters.region === region.code
                      ? 'bg-[#fabddf] text-[#2D2F4E]'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  onClick={() => handleRegionChange(region.code)}
                >
                  {region.name}
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* Capacity slider - single slider with ticks */}
        <div>
          <h3 className="font-medium text-sm text-gray-700 mb-2 flex items-center">
            <Users className="mr-1 h-4 w-4 text-[#fabddf]" />
            {t('filters.capacity')}
          </h3>
          
          <div className="px-2 py-4">
            <div className="text-center mb-2 text-sm font-medium text-gray-600">
              {capacityValue === MIN_CAPACITY 
                ? t('filters.anyCapacity')
                : t('filters.minCapacity', { count: capacityValue })}
            </div>
            
            {/* Capacity slider with tick marks */}
            <div className="mt-6 relative">
              <input
                type="range"
                min={MIN_CAPACITY}
                max={MAX_CAPACITY}
                value={capacityValue}
                onChange={handleCapacityChange}
                onMouseUp={applyCapacityFilter}
                onTouchEnd={applyCapacityFilter}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#fabddf]"
                list="capacity-ticks"
              />
              <datalist id="capacity-ticks">
                {capacityTicks.map(tick => (
                  <option key={tick} value={tick} />
                ))}
              </datalist>
              
              {/* Custom tick marks */}
              <div className="w-full flex justify-between absolute -top-2 pointer-events-none">
                {capacityTicks.map(tick => (
                  <div key={tick} className="relative">
                    <div className="h-4 w-0.5 bg-gray-300"></div>
                  </div>
                ))}
              </div>
              
              {/* Tick labels */}
              <div className="flex justify-between mt-1 text-xs text-gray-500">
                {capacityTicks.map(tick => (
                  <span key={tick} style={{
                    flexBasis: `${100/capacityTicks.length}%`,
                    textAlign: tick === MIN_CAPACITY ? 'left' : tick === MAX_CAPACITY ? 'right' : 'center'
                  }}>
                    {tick === MAX_CAPACITY ? `${tick}+` : tick}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Clear filters button */}
        {activeFilterCount > 0 && (
          <div className="flex justify-end">
            <button
              onClick={clearFilters}
              className="text-sm text-[#2D2F4E] hover:text-[#fabddf] transition-colors"
            >
              {t('filters.clearAll')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};